import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Alert, Button, ProgressBar } from 'react-bootstrap';
import { QuestionnaireForm } from '../../components/questionnaire/QuestionnaireForm';
import { SCIDQuestionnaire } from '../../components/questionnaire/SCIDQuestionnaire';
import { getQuestionnaireInfo, isValidQuestionnaireId } from '../../components/form/FormOptions';
import { getQuestionnaireQuestions, submitQuestionnaireResponse, submitQuestionnaireResponseViaToken, submitSingleAnswer, getPatientQuestionnaireAnswers, storeGDPRConsent, getGDPRConsent } from '../../api/questionnaire';
import GDPRConsentModal from '../../components/questionnaire/GDPRConsentModal';
import { LanguageSwitcher } from '../../components/shared';
import './PatientQuestionnaire.css';
import '../../components/questionnaire/SCIDQuestionnaire.css';

interface PatientQuestionnaireProps {
  patientId?: string;
  questionnaireType?: string;
  questionnaireData?: any;
  isTokenBased?: boolean;
  language?: 'en' | 'el';
  displayToken?: string;
  hasValidSession?: boolean; // True if user has returning session (skip GDPR)
}

export function PatientQuestionnaire({ 
  patientId: propPatientId, 
  questionnaireType: propQuestionnaireType, 
  questionnaireData: propQuestionnaireData,
  isTokenBased = false,
  language: propLanguage,
  displayToken,
  hasValidSession = false
}: PatientQuestionnaireProps = {}) {
  const { questionnaireId: urlQuestionnaireId, patientId: urlPatientId } = useParams();
  
  // Use props if provided (token-based), otherwise use URL params (legacy)
  const questionnaireId = propQuestionnaireType || urlQuestionnaireId;
  const patientId = propPatientId || urlPatientId;
  const navigate = useNavigate();
  const [language, setLanguage] = useState<'en' | 'el'>(propLanguage || 'el'); // Use prop language or default to Greek
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'error'>('idle');
  const [answersRecovered, setAnswersRecovered] = useState(false);
  const [showAutoSaveInfo, setShowAutoSaveInfo] = useState(true);
  const [showRecoveryNotification, setShowRecoveryNotification] = useState(false);
  const [isLanguageChangeOnly, setIsLanguageChangeOnly] = useState(false);
  
  // Timing tracking
  const [questionnaireStartTime, setQuestionnaireStartTime] = useState<Date | null>(null);
  
  // GDPR Modal state
  const [showGDPRModal, setShowGDPRModal] = useState(false);
  const [gdprConsentChecked, setGdprConsentChecked] = useState(false);
  
      // Set start time for all questionnaires when component mounts
      useEffect(() => {
        if (!questionnaireStartTime) {
          // Frontend sends local time, backend converts to Greek timezone
          setQuestionnaireStartTime(new Date());
        }
      }, [questionnaireStartTime]);

  const loadQuestionnaire = useCallback(async (shouldResetNotifications = true, loadLang: 'en' | 'el' = language) => {
    try {
      setLoading(true);
      // Check GDPR consent first (only on initial load)
      if (shouldResetNotifications && !gdprConsentChecked) {
        if (isTokenBased && hasValidSession) {
          // User has valid session from backend - already went through GDPR consent
          setGdprConsentChecked(true);
        } else if (!isTokenBased) {
          // Legacy mode - check database GDPR consent
          const consentStatus = await getGDPRConsent(patientId!, questionnaireId!);
          
          if (consentStatus) {
            setGdprConsentChecked(true);
            
            if (!consentStatus.has_consent) {
              setShowGDPRModal(true);
              setLoading(false);
              return;
            }
          } else {
            setShowGDPRModal(true);
            setLoading(false);
            return;
          }
        } else {
          // Token-based but no valid session - show GDPR modal for first-time access
          setShowGDPRModal(true);
          setLoading(false);
          return;
        }
      }
      
      let questions;
      
      // If token-based and questionnaire data is provided, use it ONLY on initial load
      // For language changes, always fetch from API to get proper translations
      if (isTokenBased && propQuestionnaireData && shouldResetNotifications) {
        questions = propQuestionnaireData.questions || propQuestionnaireData;
      } else {
        // Load questions from backend API (legacy or fallback)
        // Use the explicit loadLang param so changing `language` doesn't cause
        // the whole loadQuestionnaire callback to be re-created and re-run.
        questions = await getQuestionnaireQuestions(questionnaireId!, loadLang);
      }
      
      setQuestions(questions);
      
      // Only load existing answers and reset notifications on initial load, not on language change
      if (shouldResetNotifications) {
        // Load existing answers for recovery (both legacy and token-based questionnaires)
        const existingAnswers = await getPatientQuestionnaireAnswers(patientId!, questionnaireId!);
        if (Object.keys(existingAnswers).length > 0) {
          // Convert database values back to frontend choice values
          const convertedAnswers = convertAnswersFromBackend(existingAnswers, questionnaireId!);
          
          setAnswers(convertedAnswers);
          setAnswersRecovered(true);
          setShowRecoveryNotification(true);
          
          // Find the first unanswered question and jump to it
          const firstUnansweredIndex = questions.findIndex((q: any) => convertedAnswers[q.id] === undefined);
          if (firstUnansweredIndex !== -1) {
            setCurrentQuestionIndex(firstUnansweredIndex);
          } else {
            // If all questions are answered, go to the last question
            setCurrentQuestionIndex(questions.length - 1);
          }
        } else {
          // No existing answers - start from the beginning
          setCurrentQuestionIndex(0);
        }
      }
      
      
      if (!questions || questions.length === 0) {
        setError(`No questions found for questionnaire: ${questionnaireId}`);
        return;
      }
      
      setQuestions(questions);
    } catch (err: any) {
      console.error('Error loading questionnaire:', err);
      setError('Failed to load questionnaire: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [questionnaireId, patientId, isTokenBased, propQuestionnaireData, gdprConsentChecked]); // Removed questionnaireStartTime and language so this doesn't rerun on language changes

  // Separate effect for initial load (with notifications)
  useEffect(() => {
    if (!questionnaireId || !patientId) {
      setError('Missing questionnaire or patient information');
      setLoading(false);
      return;
    }

    if (!isValidQuestionnaireId(questionnaireId)) {
      setError('Invalid questionnaire type');
      setLoading(false);
      return;
    }

    // Initial load with notifications. Pass current language explicitly so
    // loadQuestionnaire doesn't depend on `language` and won't be recreated on language changes.
    loadQuestionnaire(true, language);
  }, [questionnaireId, patientId, loadQuestionnaire]);

  // Separate effect for language change (without notifications reset)
  useEffect(() => {
    // Only reload questions when language changes, if questionnaire is already loaded
    if (questions.length > 0 && questionnaireId && patientId) {
      setIsLanguageChangeOnly(true);
      
      const loadQuestionsForLanguage = async () => {
        try {
          setLoading(true);

          // Preserve the current question by id when switching language
          // Use the current state at the time of language change
          const currentQuestionId = questions[currentQuestionIndex]?.id;

          // Load translated questions only (skip recovery logic)
          const questionsData = await getQuestionnaireQuestions(questionnaireId!, language);

          if (!questionsData || questionsData.length === 0) {
            setError(`No questions found for questionnaire: ${questionnaireId}`);
            return;
          }

          setQuestions(questionsData);

          // Try to find the same question id in the newly loaded questions and keep the position
          if (currentQuestionId !== undefined) {
            const newIndex = questionsData.findIndex((q: any) => q.id === currentQuestionId);
            if (newIndex !== -1) {
              setCurrentQuestionIndex(newIndex);
            } else {
              // If same id not found, clamp index to available range
              setCurrentQuestionIndex(Math.min(currentQuestionIndex, Math.max(0, questionsData.length - 1)));
            }
          } else {
            setCurrentQuestionIndex(0);
          }
        } catch (err: any) {
          console.error('Error loading questionnaire for language change:', err);
          setError('Failed to load questionnaire: ' + (err.message || 'Unknown error'));
        } finally {
          setLoading(false);
          setIsLanguageChangeOnly(false);
        }
      };

      loadQuestionsForLanguage();
    }
  }, [language, questionnaireId, patientId, questions.length]); // Removed currentQuestionIndex to prevent reloading on Next/Previous

  const handleAnswerChange = async (questionId: number, answer: any) => {
    // Update local state immediately
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));

    // Auto-save to backend
    if (patientId && questionnaireId) {
      await autoSaveAnswer(questionId, answer);
    }
  };

  const autoSaveAnswer = async (questionId: number, answer: any) => {
    try {
      // Don't show loading indicator to avoid distracting the user
      // setAutoSaveStatus('saving');
      
      // Convert answer if needed (same logic as final submit)
      let finalAnswer = answer;
      if (questionnaireId === 'hamilton' || questionnaireId === 'zung' || questionnaireId === 'scid' || questionnaireId === 'aq' || questionnaireId === 'eq') {
        const convertedAnswers = convertAnswersForBackend({ [questionId]: answer }, questionnaireId);
        finalAnswer = convertedAnswers[questionId];
      }
      
      // Ensure the answer is a string for the backend
      if (typeof finalAnswer !== 'string') {
        finalAnswer = String(finalAnswer);
      }
      
      await submitSingleAnswer(patientId!, questionnaireId!, questionId, finalAnswer);
      
      // Don't show success indicator - save silently
      // setAutoSaveStatus('saved');
      // setTimeout(() => setAutoSaveStatus('idle'), 2000);
      
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('error');
      
      // Clear error status after 5 seconds
      setTimeout(() => setAutoSaveStatus('idle'), 5000);
    }
  };

  const handleNext = useCallback(() => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [currentQuestionIndex, questions.length]);

  const handlePrevious = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);

  const convertAnswersForBackend = (answers: Record<number, any>, questionnaireType: string) => {
    if (questionnaireType === 'hamilton') {
      // Hamilton value mapping: numeric to string
      const hamiltonValueMap: Record<number, string> = {
        0: "absent",
        1: "mild", 
        2: "moderate",
        3: "severe",
        4: "very_severe"
      };
      
      const convertedAnswers: Record<number, any> = {};
      for (const [questionId, answer] of Object.entries(answers)) {
        if (typeof answer === 'number' && hamiltonValueMap[answer]) {
          convertedAnswers[parseInt(questionId)] = hamiltonValueMap[answer];
        } else {
          convertedAnswers[parseInt(questionId)] = answer;
        }
      }
      return convertedAnswers;
    } 
    else if (questionnaireType === 'zung') {
      // Zung value mapping: numeric to string
      const zungValueMap: Record<number, string> = {
        1: "a_little",
        2: "some_time",
        3: "good_part",
        4: "most_time"
      };
      
      const convertedAnswers: Record<number, any> = {};
      for (const [questionId, answer] of Object.entries(answers)) {
        if (typeof answer === 'number' && zungValueMap[answer]) {
          convertedAnswers[parseInt(questionId)] = zungValueMap[answer];
        } else {
          convertedAnswers[parseInt(questionId)] = answer;
        }
      }
      return convertedAnswers;
    }
    else if (questionnaireType === 'aq') {
      // AQ: Frontend sends numeric values (0,1,2,3), backend expects text strings
      // For Greek questionnaires, map to Greek text values
      const aqValueMap: Record<number, string> = {
        0: "Συμφωνώ απόλυτα",     // Definitely agree
        1: "Συμφωνώ",             // Slightly agree  
        2: "Διαφωνώ",             // Slightly disagree
        3: "Διαφωνώ απόλυτα"      // Definitely disagree
      };
      
      const convertedAnswers: Record<number, any> = {};
      for (const [questionId, answer] of Object.entries(answers)) {
        if (typeof answer === 'number' && aqValueMap[answer]) {
          convertedAnswers[parseInt(questionId)] = aqValueMap[answer];
        } else {
          convertedAnswers[parseInt(questionId)] = answer;
        }
      }
      return convertedAnswers;
    }
    else if (questionnaireType === 'eq') {
      // EQ: Frontend sends numeric values (0,1,2,3), backend expects text strings
      // For Greek questionnaires, map to Greek text values
      const eqValueMap: Record<number, string> = {
        0: "Συμφωνώ απόλυτα",     // Strongly agree
        1: "Συμφωνώ",             // Slightly agree
        2: "Διαφωνώ",             // Slightly disagree  
        3: "Διαφωνώ απόλυτα"      // Strongly disagree
      };
      
      const convertedAnswers: Record<number, any> = {};
      for (const [questionId, answer] of Object.entries(answers)) {
        if (typeof answer === 'number' && eqValueMap[answer]) {
          convertedAnswers[parseInt(questionId)] = eqValueMap[answer];
        } else {
          convertedAnswers[parseInt(questionId)] = answer;
        }
      }
      return convertedAnswers;
    }
    else if (questionnaireType === 'scid') {
      // SCID: Keep yes/no as strings - backend expects string values
      // No conversion needed, backend validation expects "yes"/"no" strings
      return answers;
    }
    
    // For other questionnaires, return as-is
    return answers;
  };

  const convertAnswersFromBackend = (answers: Record<number, any>, questionnaireType: string) => {
    if (questionnaireType === 'hamilton') {
      // Hamilton reverse mapping: string to numeric (for frontend display)
      const hamiltonReverseMap: Record<string, number> = {
        "absent": 0,
        "mild": 1,
        "moderate": 2,
        "severe": 3,
        "very_severe": 4
      };
      
      const convertedAnswers: Record<number, any> = {};
      for (const [questionId, answer] of Object.entries(answers)) {
        if (typeof answer === 'string' && hamiltonReverseMap[answer] !== undefined) {
          convertedAnswers[parseInt(questionId)] = hamiltonReverseMap[answer];
        } else {
          convertedAnswers[parseInt(questionId)] = answer;
        }
      }
      return convertedAnswers;
    } 
    else if (questionnaireType === 'zung') {
      // Zung reverse mapping: string to numeric (for frontend display)
      const zungReverseMap: Record<string, number> = {
        "a_little": 1,
        "some_time": 2,
        "good_part": 3,
        "most_time": 4
      };
      
      const convertedAnswers: Record<number, any> = {};
      for (const [questionId, answer] of Object.entries(answers)) {
        if (typeof answer === 'string' && zungReverseMap[answer] !== undefined) {
          convertedAnswers[parseInt(questionId)] = zungReverseMap[answer];
        } else {
          convertedAnswers[parseInt(questionId)] = answer;
        }
      }
      return convertedAnswers;
    }
    else if (questionnaireType === 'aq') {
      // AQ reverse mapping: Greek text to numeric (for frontend display)
      const aqReverseMap: Record<string, number> = {
        "Συμφωνώ απόλυτα": 0,     // Definitely agree
        "Συμφωνώ": 1,             // Slightly agree
        "Διαφωνώ": 2,             // Slightly disagree
        "Διαφωνώ απόλυτα": 3      // Definitely disagree
      };
      
      const convertedAnswers: Record<number, any> = {};
      for (const [questionId, answer] of Object.entries(answers)) {
        if (typeof answer === 'string' && aqReverseMap[answer] !== undefined) {
          convertedAnswers[parseInt(questionId)] = aqReverseMap[answer];
        } else {
          convertedAnswers[parseInt(questionId)] = answer;
        }
      }
      return convertedAnswers;
    }
    else if (questionnaireType === 'eq') {
      // EQ reverse mapping: Greek text to numeric (for frontend display)
      const eqReverseMap: Record<string, number> = {
        "Συμφωνώ απόλυτα": 0,     // Strongly agree
        "Συμφωνώ": 1,             // Slightly agree
        "Διαφωνώ": 2,             // Slightly disagree
        "Διαφωνώ απόλυτα": 3      // Strongly disagree
      };
      
      const convertedAnswers: Record<number, any> = {};
      for (const [questionId, answer] of Object.entries(answers)) {
        if (typeof answer === 'string' && eqReverseMap[answer] !== undefined) {
          convertedAnswers[parseInt(questionId)] = eqReverseMap[answer];
        } else {
          convertedAnswers[parseInt(questionId)] = answer;
        }
      }
      return convertedAnswers;
    }
    else if (questionnaireType === 'scid') {
      // SCID: "yes"/"no" strings are already correct for frontend
      return answers;
    }
    
    // For other questionnaires, return as-is
    return answers;
  };

  const handleSubmit = useCallback(async () => {
    const submissionId = Math.random().toString(36).substr(2, 9);
    console.log(`🎬 handleSubmit called [ID: ${submissionId}], isSubmitting: ${isSubmitting}`);
    
    // Prevent multiple simultaneous submissions
    if (isSubmitting) {
      console.log(`⚠️  [${submissionId}] Submission already in progress, ignoring duplicate request`);
      return;
    }

    try {
      setIsSubmitting(true);
      console.log(`🚀 [${submissionId}] Starting questionnaire submission...`);
      
      // Convert answers to expected backend format for questionnaires that need it
      let finalAnswers = answers;
      if (questionnaireId === 'hamilton' || questionnaireId === 'zung' || questionnaireId === 'scid' || questionnaireId === 'aq' || questionnaireId === 'eq') {
        finalAnswers = convertAnswersForBackend(answers, questionnaireId);
      }

      console.log(`📤 [${submissionId}] Sending request to backend...`);
      
      // Submit answers to backend with timing information
      if (isTokenBased && displayToken) {
        // Use token-based submission for token questionnaires
        await submitQuestionnaireResponseViaToken({
          questionnaireId: questionnaireId!,
          answers: finalAnswers,
          startedAt: questionnaireStartTime,
          completedAt: new Date(),  // Frontend sends local time, backend converts to Greek timezone
          displayToken: displayToken
        });
      } else {
        // Use regular submission for legacy questionnaires
        await submitQuestionnaireResponse({
          patientId: patientId!,
          questionnaireId: questionnaireId!,
          answers: finalAnswers,
          startedAt: questionnaireStartTime,
          completedAt: new Date()  // Frontend sends local time, backend converts to Greek timezone
        });
      }
      
      console.log(`✅ [${submissionId}] Questionnaire submitted successfully`);
      
      // Store language in sessionStorage for the completion page
      sessionStorage.setItem('questionnaireCompletionLanguage', language);
      
      // Navigate to completion page
      navigate(`/questionnaire-complete?lang=${language}`);
    } catch (err: any) {
      console.error(`❌ [${submissionId}] Failed to submit questionnaire:`, err);
      setError('Failed to submit questionnaire: ' + (err.message || 'Unknown error'));
      setIsSubmitting(false); // Re-enable submission on error
    }
  }, [patientId, questionnaireId, answers, questions, isTokenBased, displayToken, language, navigate, isSubmitting, questionnaireStartTime]);

  // Handle keyboard events
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't interfere with actual text inputs and textareas
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'TEXTAREA' || 
        (target.tagName === 'INPUT' && target.getAttribute('type') === 'text'))) {
      return;
    }
    
    // CRITICAL FIX: Don't handle Enter on buttons - they have their own onClick
    // This prevents double submission when clicking the submit button
    if (target && target.tagName === 'BUTTON') {
      console.log('⚠️  Ignoring Enter key on button (button onClick will handle it)');
      return;
    }
    
    // Handle Enter key for next/submit
    if (event.key === 'Enter') {
      event.preventDefault();
      
      const currentQuestion = questions[currentQuestionIndex];
      const hasAnswer = answers[currentQuestion?.id] !== undefined;
      
      console.log('Enter pressed:', { 
        hasAnswer, 
        currentQuestionIndex, 
        totalQuestions: questions.length,
        targetTag: target?.tagName,
        targetType: target?.getAttribute('type')
      });
      
      if (hasAnswer) {
        if (currentQuestionIndex === questions.length - 1) {
          // Submit if on last question
          console.log('Submitting questionnaire via Enter key');
          handleSubmit();
        } else {
          // Move to next question
          console.log('Moving to next question via Enter key');
          handleNext();
        }
      }
    }
    
    // Handle Backspace key for previous question
    if (event.key === 'Backspace') {
      // Don't handle Backspace on buttons
      if (target && target.tagName === 'BUTTON') {
        return;
      }
      
      event.preventDefault();
      
      console.log('Backspace pressed:', { 
        currentQuestionIndex, 
        canGoBack: currentQuestionIndex > 0,
        targetTag: target?.tagName,
        targetType: target?.getAttribute('type')
      });
      
      if (currentQuestionIndex > 0) {
        console.log('Moving to previous question via Backspace key');
        handlePrevious();
      }
    }
  }, [currentQuestionIndex, questions, answers, handleNext, handleSubmit, handlePrevious]);

  // GDPR Modal handlers
  const handleGDPRConsentGiven = useCallback(async (consent: boolean) => {
    console.log('GDPR consent given:', consent);
    
    try {
      // Store GDPR consent in backend
      await storeGDPRConsent(patientId!, questionnaireId!, consent);
      console.log('GDPR consent stored successfully');
      
      setGdprConsentChecked(true);
      setShowGDPRModal(false);
      
      // If consent was given, reload questionnaire
      if (consent) {
        // Reload questionnaire after a short delay to ensure state is updated
        setTimeout(() => {
          loadQuestionnaire(false);
        }, 100);
      } else {
        // If consent was not given, redirect to error page
        console.log('GDPR consent not given, redirecting to error page');
        navigate('/error?type=gdpr_declined');
      }
    } catch (error) {
      console.error('Failed to store GDPR consent:', error);
      setError(language === 'el' 
        ? 'Σφάλμα κατά την αποθήκευση της συναίνεσης. Παρακαλώ δοκιμάστε ξανά.'
        : 'Error storing consent. Please try again.'
      );
    }
  }, [patientId, questionnaireId, language, navigate, loadQuestionnaire]);


  // Add keyboard event listener
  useEffect(() => {
    console.log('🎯 Adding keydown event listener');
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      console.log('🧹 Removing keydown event listener');
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const questionnaireInfo = getQuestionnaireInfo(questionnaireId!);
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;
  const currentQuestion = questions[currentQuestionIndex];
  
  // Debug log the current question
  console.log('Current question index:', currentQuestionIndex);
  console.log('Current question:', currentQuestion);
  console.log('Total questions:', questions.length);
  console.log('Current answer for question:', currentQuestion?.id, '=', answers[currentQuestion?.id]);
  console.log('Next button disabled?', answers[currentQuestion?.id] === undefined);

  if (loading) {
    return (
      <div className="patient-questionnaire-container">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-3">Loading questionnaire...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patient-questionnaire-container">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => navigate('/')}>
            Return to Home
          </Button>
        </Alert>
      </div>
    );
  }

  // Note: Completion now navigates to separate page, no inline display needed

  return (
    <div className="patient-questionnaire-container">
      {/* GDPR Consent Modal */}
      <GDPRConsentModal
        isOpen={showGDPRModal}
        onClose={() => setShowGDPRModal(false)}
        onConsentGiven={handleGDPRConsentGiven}
        language={language}
      />
        {/* Compact header with floating language switcher */}
        <div className="compact-header mb-3">
          <div className="d-flex justify-content-between align-items-start">
            <div className="header-content">
              <h5 className="questionnaire-title mb-1">
                {questionnaireInfo?.title[language] || 'Questionnaire'}
              </h5>
              {/* Auto-save status indicator - only show errors */}
              {autoSaveStatus === 'error' && (
                <div className="auto-save-status-compact">
                  <span className="save-indicator error">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    <span className="d-none d-sm-inline">
                      {language === 'el' ? 'Σφάλμα αποθήκευσης απάντησης' : 'There was an error saving the answer to the database'}
                    </span>
                  </span>
                </div>
              )}
            </div>
            {/* Language switcher using universal component */}
            <LanguageSwitcher
              selectedLanguage={language}
              onLanguageChange={setLanguage}
              variant="compact"
              className="questionnaire-lang-switcher"
            />
          </div>
        </div>

        {/* Auto-save information banner */}
        {showAutoSaveInfo && (
          <div className="alert alert-info alert-dismissible fade show" role="alert">
            <i className="bi bi-info-circle me-2"></i>
            <strong>
              {language === 'el' ? 'Αυτόματη Αποθήκευση: ' : 'Auto-save: '}
            </strong>
            {language === 'el' 
              ? 'Οι απαντήσεις σας αποθηκεύονται αυτόματα καθώς προχωράτε. Μπορείτε να επιστρέψετε αργότερα και να συνεχίσετε από εκεί που σταματήσατε.'
              : 'Your answers are automatically saved as you progress. You can return later and continue where you left off.'
            }
            <button 
              type="button" 
              className="btn-close" 
              aria-label="Close"
              onClick={() => setShowAutoSaveInfo(false)}
            ></button>
          </div>
        )}

        {/* Recovery notification */}
        {answersRecovered && showRecoveryNotification && !isLanguageChangeOnly && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <i className="bi bi-arrow-clockwise me-2"></i>
            <strong>
              {language === 'el' ? 'Απαντήσεις Ανακτήθηκαν: ' : 'Answers Recovered: '}
            </strong>
            {language === 'el' 
              ? `Βρέθηκαν ${Object.keys(answers).length} προηγούμενες απαντήσεις και ανακτήθηκαν αυτόματα. Θα συνεχίσετε από την ερώτηση ${currentQuestionIndex + 1}.`
              : `Found and recovered ${Object.keys(answers).length} previous answers. You'll continue from question ${currentQuestionIndex + 1}.`
            }
            <button 
              type="button" 
              className="btn-close" 
              aria-label="Close"
              onClick={() => setShowRecoveryNotification(false)}
            ></button>
          </div>
        )}

        {/* SCID Questionnaire - Special Layout */}
        {questionnaireId === 'scid' ? (
          <>
            {/* Questionnaire description for SCID */}
            <Card className="mb-4">
              <Card.Body>
                <Card.Text>
                  {questionnaireInfo?.description[language]}
                </Card.Text>
                <Card.Text className="small text-muted">
                  {language === 'el' 
                    ? 'Αυτό το ερωτηματολόγιο περιέχει 106 ερωτήσεις. Παρακαλώ απαντήστε με "Ναι" ή "Όχι" σε κάθε ερώτηση.'
                    : 'This questionnaire contains 106 questions. Please answer "Yes" or "No" to each question.'
                  }
                </Card.Text>
              </Card.Body>
            </Card>

            {/* SCID specialized component */}
            <SCIDQuestionnaire
              questions={questions}
              language={language}
              answers={answers}
              onAnswerChange={handleAnswerChange}
            />

            {/* Submit button for SCID */}
            <div className="d-flex justify-content-center mt-4 mb-5">
              {(() => {
                const hasTranslationIssues = language === 'en' && questions.some(q => !q.text || q.text.trim() === '');
                const isComplete = Object.keys(answers).length === questions.length;
                const isDisabled = !isComplete || hasTranslationIssues || isSubmitting;
                
                return (
                  <Button 
                    variant="success" 
                    size="lg"
                    onClick={handleSubmit}
                    disabled={isDisabled}
                    style={{ minWidth: '200px' }}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="bi bi-check2 me-2"></i>
                        <span>{language === 'el' ? 'Υποβολή' : 'Submitting'}</span>
                        <span className="loading-dots"></span>
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check2 me-2"></i>
                        {language === 'el' ? 'Υποβολή Ερωτηματολογίου' : 'Submit Questionnaire'}
                      </>
                    )}
                    {hasTranslationIssues && (
                      <div className="small text-muted d-block mt-1">
                        Please switch to Greek to submit
                      </div>
                    )}
                  </Button>
                );
              })()}
            </div>
          </>
        ) : (
          <>
            {/* Regular questionnaire layout for other questionnaires */}
            
            {/* Progress bar */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small text-muted">
                  {language === 'el' ? 'Πρόοδος' : 'Progress'}
                </span>
                <span className="small text-muted">
                  {currentQuestionIndex + 1} / {questions.length}
                </span>
              </div>
              <ProgressBar now={progress} className="mb-2" />
            </div>

            {/* Questionnaire description */}
            {currentQuestionIndex === 0 && (
              <Card className="mb-4">
                <Card.Body>
                  <Card.Text>
                    {questionnaireInfo?.description[language]}
                  </Card.Text>
                  <Card.Text className="small text-muted">
                    {questionnaireInfo?.instructions[language]}
                  </Card.Text>
                </Card.Body>
              </Card>
            )}

            {/* Current question */}
            {currentQuestion && (
              <QuestionnaireForm
                question={currentQuestion}
                language={language}
                answer={answers[currentQuestion.id]}
                onAnswerChange={(answer: any) => handleAnswerChange(currentQuestion.id, answer)}
              />
            )}

            {/* Navigation buttons */}
            <div className="navigation-buttons mt-4">
              <Button 
                variant="outline-secondary" 
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
                className="nav-btn nav-btn-previous"
                size="lg"
              >
                <i className="bi bi-arrow-left me-2"></i>
                {language === 'el' ? 'Προηγούμενο' : 'Previous'}
              </Button>

              {currentQuestionIndex === questions.length - 1 ? (
                <Button 
                  variant="success" 
                  onClick={handleSubmit}
                  disabled={answers[currentQuestion?.id] === undefined || isSubmitting}
                  className="nav-btn nav-btn-submit"
                  size="lg"
                  style={{ minWidth: '130px' }}
                >
                  {isSubmitting ? (
                    <>
                      <i className="bi bi-check2 me-2"></i>
                      <span>{language === 'el' ? 'Υποβολή' : 'Submitting'}</span>
                      <span className="loading-dots"></span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check2 me-2"></i>
                      {language === 'el' ? 'Υποβολή' : 'Submit'}
                    </>
                  )}
                </Button>
              ) : (
                <Button 
                  variant="primary" 
                  onClick={handleNext}
                  disabled={answers[currentQuestion?.id] === undefined}
                  className="nav-btn nav-btn-next"
                  size="lg"
                >
                  {language === 'el' ? 'Επόμενο' : 'Next'}
                  <i className="bi bi-arrow-right ms-2"></i>
                </Button>
              )}
            </div>
          </>
        )}
    </div>
  );
}
